import React, { useCallback, useEffect, useRef, useState } from 'react';

// Canvas-based signature pad.  Captures mouse + touch strokes and exposes the
// drawing as a base64 PNG data URL via `onChange`.  Empty drawing emits ''.
//
// Usage:
//   <SignaturePad value={data.inspectorSignature} onChange={(v) => update('inspectorSignature', v)} />

const CANVAS_WIDTH = 500;
const CANVAS_HEIGHT = 140;
const STROKE_COLOUR = '#0f172a'; // near-black so it scans cleanly
const STROKE_WIDTH = 2;

const SignaturePad = ({ value, onChange, label = 'Inspector Signature', required, disabled = false, disabledNotice }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const hasInkRef = useRef(false);
  const [showHint, setShowHint] = useState(!value);

  // Reset internal flag if the parent clears the value externally.
  useEffect(() => {
    if (!value) {
      hasInkRef.current = false;
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      setShowHint(true);
    }
  }, [value]);

  // Configure the canvas's drawing context once on mount.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = STROKE_COLOUR;
    ctx.lineWidth = STROKE_WIDTH;
  }, []);

  const getPoint = useCallback((evt) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }, []);

  const startStroke = useCallback((evt) => {
    evt.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPoint(evt);
    setShowHint(false);
  }, [getPoint]);

  const moveStroke = useCallback((evt) => {
    if (!drawingRef.current) return;
    evt.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const point = getPoint(evt);
    const last = lastPointRef.current;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    hasInkRef.current = true;
  }, [getPoint]);

  const endStroke = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (hasInkRef.current) {
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onChange?.(dataUrl);
    }
  }, [onChange]);

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasInkRef.current = false;
    setShowHint(true);
    onChange?.('');
  };

  if (disabled) {
    return (
      <div className="md:col-span-2">
        <span className="block text-sm font-medium text-pvs-text-muted mb-2">{label}</span>
        <div className="border border-pvs-border rounded-lg bg-white/5 px-4 py-6 text-center">
          <p className="text-pvs-text-muted text-sm font-semibold">This is not a certificate</p>
          {disabledNotice && (
            <p className="text-pvs-text-dim text-xs mt-1">{disabledNotice}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="md:col-span-2">
      <span className="block text-sm font-medium text-pvs-text-muted mb-2">
        {label}
        {required && <span className="text-red-400"> *</span>}
      </span>
      <div className="relative border border-pvs-border rounded-lg bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="block w-full touch-none cursor-crosshair"
          style={{ aspectRatio: `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}` }}
          onMouseDown={startStroke}
          onMouseMove={moveStroke}
          onMouseUp={endStroke}
          onMouseLeave={endStroke}
          onTouchStart={startStroke}
          onTouchMove={moveStroke}
          onTouchEnd={endStroke}
        />
        {showHint && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-400 text-sm select-none">
            Sign here
          </div>
        )}
      </div>
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-pvs-text-dim">Use mouse, finger, or stylus to sign.</span>
        <button
          type="button"
          onClick={handleClear}
          className="text-xs px-3 py-1 border border-pvs-border rounded text-white hover:bg-white/5"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
